// Live walk tracking on a phone using the browser's GPS. The walk itself is
// an ordinary `walk` event (so it syncs and shows on the kiosk live); this
// module just feeds distance and a simplified route into it while it runs.
//
// Browsers only deliver GPS while the page is open, so tracking holds a
// screen wake lock and tells you if the phone went to sleep.

import { useSyncExternalStore } from 'react';
import { endTimed, openDuration, startTimed, updateEvent } from '../../data/actions.ts';
import { data } from '../../data/index.ts';
import { acceptFix, round5, routeLengthM, simplify, type LatLng } from '../../domain/geo.ts';
import type { LogEvent } from '../../domain/types.ts';

export interface GpsState {
  tracking: boolean;
  walkId?: string;
  route: LatLng[];
  distanceKm: number;
  accuracy?: number;
  error?: string;
  lastFixAt?: number;
}

let state: GpsState = { tracking: false, route: [], distanceKm: 0 };
let watchId: number | undefined;
let wake: WakeLockSentinel | undefined;
let saveTimer: ReturnType<typeof setInterval> | undefined;
const listeners = new Set<() => void>();

function set(patch: Partial<GpsState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

export function useGps(): GpsState {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
  );
}

export const gpsAvailable = typeof navigator !== 'undefined' && 'geolocation' in navigator;

/** Start a walk (or a run together); with gps=true also start recording distance and route. */
export function startWalk(gps: boolean, run = false): LogEvent<'walk'> {
  let walk = startTimed('walk') as LogEvent<'walk'>;
  if (run && !walk.data.run) {
    walk = { ...walk, data: { ...walk.data, run: true } };
    updateEvent(walk);
  }
  if (gps && gpsAvailable) startGps(walk);
  return walk;
}

/** Resume GPS on an already-running walk (e.g. after a page reload). */
export function startGps(walk: LogEvent<'walk'>) {
  stopWatch();
  const existing = walk.data.route ?? [];
  set({ tracking: true, walkId: walk.id, route: existing, distanceKm: walk.data.distanceKm ?? 0, error: undefined });
  watchId = navigator.geolocation.watchPosition(
    (pos) => {
      const next: LatLng = [pos.coords.latitude, pos.coords.longitude];
      const prev = state.route[state.route.length - 1];
      const secs = state.lastFixAt ? (pos.timestamp - state.lastFixAt) / 1000 : 0;
      set({ accuracy: pos.coords.accuracy });
      if (!acceptFix(prev, next, pos.coords.accuracy, secs)) return;
      const route = [...state.route, round5(next)];
      set({ route, distanceKm: routeLengthM(route) / 1000, lastFixAt: pos.timestamp, error: undefined });
    },
    (err) => set({ error: err.code === err.PERMISSION_DENIED ? 'Location permission was denied — walks will record time only.' : 'Waiting for GPS…' }),
    { enableHighAccuracy: true, maximumAge: 5000, timeout: 30_000 },
  );
  navigator.wakeLock
    ?.request('screen')
    .then((w) => (wake = w))
    .catch(() => undefined);
  saveTimer = setInterval(save, 30_000);
}

function save() {
  if (!state.walkId) return;
  const cur = data().store.get('events', state.walkId);
  if (!cur || cur.type !== 'walk') return;
  updateEvent({ ...cur, data: { ...cur.data, distanceKm: round3(state.distanceKm), route: simplify(state.route, 3) } });
}

function stopWatch() {
  if (watchId !== undefined) navigator.geolocation.clearWatch(watchId);
  watchId = undefined;
  clearInterval(saveTimer);
  void wake?.release().catch(() => undefined);
  wake = undefined;
}

/** Finish the walk: save final distance/route and close the event. */
export function finishWalk(): LogEvent | undefined {
  if (state.tracking) save();
  stopWatch();
  set({ tracking: false, walkId: undefined, route: [], distanceKm: 0, accuracy: undefined, lastFixAt: undefined });
  return endTimed('walk');
}

export function currentWalk(): LogEvent<'walk'> | undefined {
  return openDuration('walk') as LogEvent<'walk'> | undefined;
}

function round3(n: number) {
  return Math.round(n * 1000) / 1000;
}
